import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel,
  MenuItem, Paper, Select, Switch, Table, TableBody, TableCell, TableHead,
  TablePagination, TableRow, TextField, Tooltip, Typography, Link,
} from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPatch, getConfigPost } from "../getConfig";

interface AwinBrand {
  id: number;
  advertiser_id: string;
  display_name: string;
  logo_url: string | null;
  currency: string | null;
  status: string;
  display_url: string | null;
  relationship: string | null;
  link_status: string | null;
  primary_sector: string | null;
  region_code: string | null;
  rate_numeric: number | null;
  rate_model: string | null;
  rate_display: string | null;
  validation_days: number | null;
  approval_percentage: number | null;
  deeplink_enabled: number | null;
  haircut_percentage: number | null;
  clo_enabled: number | null;
  last_rate_sync_at: string | null;
  last_sync_at: string | null;
  is_live: boolean;
}

interface Metadata {
  total_brands: number;
  enabled_count: number;
  non_gbp_count: number;
  unrated_count: number;
  no_validation_days_count: number;
  joined_count: number;
  live_count: number;
}

const EMPTY_METADATA: Metadata = {
  total_brands: 0, enabled_count: 0, non_gbp_count: 0, unrated_count: 0,
  no_validation_days_count: 0, joined_count: 0, live_count: 0,
};

const RELATIONSHIPS = ["joined", "pending", "notjoined", "suspended", "rejected"];

const AwinBrandsDashboard: React.FC = () => {
  const [brands, setBrands] = useState<AwinBrand[]>([]);
  const [metadata, setMetadata] = useState<Metadata>(EMPTY_METADATA);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState<number | null>(null);

  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [testBrand, setTestBrand] = useState<AwinBrand | null>(null);
  const [testResult, setTestResult] = useState<any | null>(null);
  const [testError, setTestError] = useState("");
  const [testLoading, setTestLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [relationshipFilter, setRelationshipFilter] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("");
  const [unratedOnly, setUnratedOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);

  const fetchBrands = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params: any = { page: page + 1, pageSize, sortBy: "display_name", sortOrder: "ASC" };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (relationshipFilter) params.relationship = relationshipFilter;
      if (currencyFilter) params.currency = currencyFilter;
      if (unratedOnly) params.unrated = "true";

      const response = await axios.get<any>(ENDPOINTS.AWIN_BRANDS, getConfigGet(params));
      if (response.data?.success) {
        setBrands(response.data.data || []);
        setTotal(response.data.pagination?.total || 0);
        setMetadata(response.data.metadata || EMPTY_METADATA);
      } else {
        setError(response.data?.message || "Failed to load Awin brands");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load Awin brands");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, statusFilter, relationshipFilter, currencyFilter, unratedOnly]);

  useEffect(() => {
    const timer = setTimeout(fetchBrands, 300);
    return () => clearTimeout(timer);
  }, [fetchBrands]);

  const toggleStatus = async (brand: AwinBrand) => {
    const next = brand.status === "enabled" ? "disabled" : "enabled";
    setSavingId(brand.id);
    setError("");
    try {
      await axios.patch(`${ENDPOINTS.AWIN_BRANDS}/${brand.id}`, { status: next }, { headers: getConfigPatch() });
      setBrands((prev) => prev.map((b) => (b.id === brand.id
        ? { ...b, status: next, is_live: next === "enabled" && b.relationship === "joined" }
        : b)));
      setMetadata((m) => ({
        ...m,
        enabled_count: m.enabled_count + (next === "enabled" ? 1 : -1),
        live_count: brand.relationship === "joined"
          ? m.live_count + (next === "enabled" ? 1 : -1)
          : m.live_count,
      }));
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to update brand");
    } finally {
      setSavingId(null);
    }
  };

  const openDetails = async (brand: AwinBrand) => {
    setDetail({ id: brand.id, display_name: brand.display_name });
    setDetailLoading(true);
    try {
      const response = await axios.get<any>(`${ENDPOINTS.AWIN_BRANDS}/${brand.id}`, getConfigGet({}));
      setDetail(response.data?.data || response.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load brand details");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const runLinkTest = async (brand: AwinBrand) => {
    setTestBrand(brand);
    setTestResult(null);
    setTestError("");
    setTestLoading(true);
    try {
      const response = await axios.post<any>(
        ENDPOINTS.AWIN_BRANDS,
        { brand_id: brand.id, action: "test_link" },
        { headers: getConfigPost() }
      );
      setTestResult(response.data?.data || response.data);
    } catch (err: any) {
      setTestError(err?.response?.data?.message || err?.message || "Failed to create tracking link");
    } finally {
      setTestLoading(false);
    }
  };

  const detailRow = (label: string, value: any) => (
    <Grid container spacing={1} key={label} style={{ padding: "4px 0" }}>
      <Grid item xs={5}>
        <Typography variant="body2" color="textSecondary">{label}</Typography>
      </Grid>
      <Grid item xs={7}>
        <Typography variant="body2" style={{ wordBreak: "break-all" }}>
          {value === null || value === undefined || value === "" ? "—" : String(value)}
        </Typography>
      </Grid>
    </Grid>
  );

  const stat = (label: string, value: number | string) => (
    <Grid item xs={6} sm={3}>
      <Card>
        <CardContent>
          <Typography variant="body2" color="textSecondary">{label}</Typography>
          <Typography variant="h5">{value}</Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>Awin Brands</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Programmes sync in disabled. Only joined programmes can be enabled — join them in the Awin console first.
      </Typography>

      <Grid container spacing={2} style={{ marginTop: 8, marginBottom: 8 }}>
        {stat("Total brands", metadata.total_brands)}
        {stat("Joined", metadata.joined_count)}
        {stat("Unrated", metadata.unrated_count)}
        {stat("Live in app", metadata.live_count)}
      </Grid>

      <Paper style={{ padding: 16, marginBottom: 16 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth label="Search brand" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as string); setPage(0); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="enabled">Enabled</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Relationship</InputLabel>
              <Select
                value={relationshipFilter}
                onChange={(e) => { setRelationshipFilter(e.target.value as string); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                {RELATIONSHIPS.map((r) => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select value={currencyFilter} onChange={(e) => { setCurrencyFilter(e.target.value as string); setPage(0); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="GBP">GBP</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Tooltip title="Programmes Awin has not returned a commission rate for">
              <Box display="flex" alignItems="center">
                <Switch checked={unratedOnly} onChange={(e) => { setUnratedOnly(e.target.checked); setPage(0); }} />
                <Typography variant="body2">Unrated only</Typography>
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {error && <Typography color="error" gutterBottom>{error}</Typography>}

      <Paper>
        {loading && <Box p={2} textAlign="center"><CircularProgress size={28} /></Box>}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Brand</TableCell>
              <TableCell>Advertiser</TableCell>
              <TableCell>Sector</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Rate</TableCell>
              <TableCell align="right">Validation days</TableCell>
              <TableCell align="right">Approval %</TableCell>
              <TableCell>Relationship</TableCell>
              <TableCell align="center">Live in app</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && brands.length === 0 && (
              <TableRow><TableCell colSpan={10} align="center">No brands found</TableCell></TableRow>
            )}
            {brands.map((b) => (
              <TableRow key={b.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    {b.logo_url && (
                      <img src={b.logo_url} alt="" width={28} height={28}
                           style={{ marginRight: 8, objectFit: "contain" }} />
                    )}
                    <Box>
                      {b.display_url
                        ? <Link href={b.display_url} target="_blank" rel="noopener">{b.display_name}</Link>
                        : <span>{b.display_name}</span>}
                      {b.clo_enabled ? (
                        <Tooltip title="Card-linked offer — this brand should not be click-tracked">
                          <Chip label="CLO" size="small" color="secondary" style={{ marginLeft: 8 }} />
                        </Tooltip>
                      ) : null}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{b.advertiser_id}</TableCell>
                <TableCell>{b.primary_sector || "—"}</TableCell>
                <TableCell>
                  <Chip label={b.currency || "-"} size="small"
                        color={b.currency === "GBP" ? "primary" : "default"} />
                </TableCell>
                <TableCell>
                  {b.rate_display || <Typography variant="body2" color="textSecondary">—</Typography>}
                </TableCell>
                <TableCell align="right">
                  {b.validation_days === null
                    ? <Tooltip title="Falls back to the configured default"><span>—</span></Tooltip>
                    : b.validation_days}
                </TableCell>
                <TableCell align="right">
                  {b.approval_percentage === null ? "—" : `${b.approval_percentage}%`}
                </TableCell>
                <TableCell>
                  <Chip
                    label={b.relationship || "-"}
                    size="small"
                    color={b.relationship === "joined" ? "primary" : "default"}
                  />
                </TableCell>
                <TableCell align="center">
                  {savingId === b.id
                    ? <CircularProgress size={18} />
                    : (
                      <Tooltip title={b.relationship === "joined" ? "" : "Join the programme in the Awin console first"}>
                        <span>
                          <Switch
                            checked={b.status === "enabled"}
                            onChange={() => toggleStatus(b)}
                            color="primary"
                            disabled={b.relationship !== "joined" && b.status !== "enabled"}
                          />
                        </span>
                      </Tooltip>
                    )}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => openDetails(b)}>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={b.relationship === "joined" ? "Test redirect link" : "Programme must be joined first"}>
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => runLinkTest(b)}
                        disabled={b.relationship !== "joined"}
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onChangePage={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onChangeRowsPerPage={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{detail?.display_name || "Brand details"}</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box p={2} textAlign="center"><CircularProgress size={28} /></Box>
          ) : (
            <>
              {detailRow("Brand id", detail?.id)}
              {detailRow("Advertiser id", detail?.advertiser_id)}
              {detailRow("Status", detail?.status)}
              {detailRow("Relationship", detail?.relationship)}
              {detailRow("Link status", detail?.link_status)}
              {detailRow("Currency", detail?.currency)}
              {detailRow("Rate", detail?.discount_rate)}
              {detailRow("Rate numeric", detail?.rate_numeric)}
              {detailRow("Rate model", detail?.rate_model)}
              {detailRow("Validation days", detail?.validation_days)}
              {detailRow("Approval %", detail?.approval_percentage)}
              {detailRow("Average payment time", detail?.average_payment_time)}
              {detailRow("Sprive haircut %", detail?.haircut_percentage)}
              {detailRow("CLO enabled", detail?.clo_enabled ? "yes" : "no")}
              {detailRow("Deeplink enabled", detail?.deeplink_enabled ? "yes" : "no")}
              {detailRow("Primary sector", detail?.primary_sector)}
              {detailRow("Region", detail?.region_code)}
              {detailRow("Categories", detail?.categories && JSON.stringify(detail.categories))}
              {detailRow("Valid domains", detail?.valid_domains && JSON.stringify(detail.valid_domains))}
              {detailRow("Website", detail?.display_url)}
              {detailRow("Click-through url", detail?.click_through_url)}
              {detailRow("Logo", detail?.logo_url)}
              {detailRow("Last rate sync", detail?.last_rate_sync_at)}
              {detailRow("Last sync", detail?.last_sync_at)}
              {detailRow("Description", detail?.description)}
              {detail?.rate_breakdown && (
                <Box mt={2}>
                  <Typography variant="body2" color="textSecondary">Commission groups</Typography>
                  <pre style={{ fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                    {JSON.stringify(detail.rate_breakdown, null, 2)}
                  </pre>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!testBrand} onClose={() => setTestBrand(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Test redirect — {testBrand?.display_name}</DialogTitle>
        <DialogContent dividers>
          {testLoading && <Box p={2} textAlign="center"><CircularProgress size={28} /></Box>}
          {!testLoading && testError && <Typography color="error">{testError}</Typography>}
          {!testLoading && testResult && (
            <>
              {detailRow("Advertiser id", testResult.advertiser_id)}
              {detailRow("Relationship", testResult.relationship)}
              {detailRow("Deeplinked", testResult.deeplinked ? "yes" : "no — lands on the programme home page")}
              {detailRow("Destination", testResult.destination_url)}
              {detailRow("clickRef", testResult.click_ref)}
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">Tracking url</Typography>
                <Link href={testResult.tracking_url} target="_blank" rel="noopener"
                      style={{ wordBreak: "break-all" }}>
                  {testResult.tracking_url}
                </Link>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {testBrand && (
            <Button onClick={() => runLinkTest(testBrand)} disabled={testLoading}>Retry</Button>
          )}
          <Button onClick={() => setTestBrand(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AwinBrandsDashboard;
